import {
  getConfidenceColor,
  getCellLowestConfidence,
  getCellTextWithConfidenceFilter,
} from "../utils/confidenceUtils";
import {
  hasMultipleRowPages,
  getVisibleRowPageMap,
  getVisiblePageGroups,
} from "../utils/pageIndicatorUtils";
import { extractOriginalIndexFromId } from "../utils/pdfTableViewerUtils";
import CellContent from "./CellContent";
import ExcelGridNavigationBar from "./ExcelGridNavigationBar";
import SummaryStats from "./SummaryStats";
import { TableChart } from "@mui/icons-material";
import { Box, Typography, IconButton, Chip, useTheme } from "@mui/material";
import React, { memo, useMemo } from "react";

/**
 * ExcelGrid component for rendering the main table structure
 * Displays table data in an Excel-like grid format with headers, stats, and interactive cells
 */
const ExcelGrid = ({
  selectedTable,
  tableData,
  editingCell,
  selectedCell,
  editValue,
  setEditValue,
  saveCellEdit,
  cancelCellEdit,
  startCellEdit,
  setCellInfoAnchor,
  isMultiPageTable,
  getTablePages,
  currentPage,
  setCurrentPage,
  showStats,
  setShowStats,
  showConfidence,
  setShowConfidence,
  calculateColumnStats,
  handleCellClick,
  removeColumnGlobally,
  removeRow,
  rowIds,
  // Zoom prop
  tableZoom = 0.8,
  setTableZoom,
  // Confidence filtering
  confidenceFilterEnabled,
  setConfidenceFilterEnabled,
  confidenceThreshold,
  setConfidenceThreshold,
  // Recovery-related props
  deletedRows,
  deletedColumns,
  recoverRowById,
  recoverColumnById,
  recoverAllRows,
  recoverAllColumns,
}) => {
  const theme = useTheme();
  const borderColor = theme.brand.gray.gainsboro;
  // Memoize expensive page calculations to prevent recalculation on every render
  // Handle null cases in the memoization logic to avoid calling hooks conditionally
  const rowPageMap = useMemo(
    () =>
      selectedTable && tableData && rowIds
        ? getVisibleRowPageMap(selectedTable, tableData, rowIds)
        : {},
    [selectedTable, tableData, rowIds]
  );

  const showPageIndicators = useMemo(
    () =>
      selectedTable && tableData ? hasMultipleRowPages(rowPageMap) : false,
    [selectedTable, tableData, rowPageMap]
  );

  const pageGroups = useMemo(
    () =>
      selectedTable && tableData && showPageIndicators
        ? getVisiblePageGroups(rowPageMap, tableData.rows?.length || 0)
        : {},
    [selectedTable, tableData, showPageIndicators, rowPageMap]
  );

  // Cell style definition - memoized to prevent unnecessary re-renders
  const cellStyle = useMemo(
    () => ({
      borderWidth: "1px",
      borderStyle: "solid",
      borderColor,
      padding: "12px",
      maxWidth: "200px",
      overflow: "hidden",
      whiteSpace: "nowrap",
      cursor: "pointer",
      transition: "background-color 0.2s, max-width 0.2s",
    }),
    [borderColor]
  );

  // Memoize table rows rendering with null safety
  const memoizedTableRows = useMemo(() => {
    if (!selectedTable || !tableData?.rows) return [];

    return tableData.rows.map((row, rowIndex) => (
      <tr key={rowIndex}>
        {/* Page indicator cell - only render for first row of each page group */}
        {showPageIndicators && pageGroups[rowIndex + 1]?.isFirst && (
          <td
            rowSpan={pageGroups[rowIndex + 1].rowspan}
            onClick={() => setCurrentPage(pageGroups[rowIndex + 1].page)}
            style={{
              borderWidth: "1px",
              borderStyle: "solid",
              borderColor,
              padding: "8px",
              backgroundColor: "#fafafa",
              textAlign: "center",
              verticalAlign: "middle",
              position: "sticky",
              left: 0,
              zIndex: 1,
              minWidth: "60px",
              maxWidth: "60px",
              whiteSpace: "nowrap",
              cursor: "pointer",
            }}
          >
            <Chip
              label={pageGroups[rowIndex + 1].page || "?"}
              size={"small"}
              color={
                pageGroups[rowIndex + 1].page === currentPage
                  ? "warning"
                  : "default"
              }
              sx={{
                fontSize: "11px",
                height: "24px",
                minWidth: "28px",
                fontWeight:
                  pageGroups[rowIndex + 1].page === currentPage
                    ? "bold"
                    : "normal",
                backgroundColor:
                  pageGroups[rowIndex + 1].page === currentPage
                    ? "#ff9800"
                    : "rgba(0, 0, 0, 0.08)",
                color:
                  pageGroups[rowIndex + 1].page === currentPage
                    ? "#fff"
                    : "rgba(0, 0, 0, 0.87)",
                "& .MuiChip-label": {
                  px: 0.75,
                },
                "&:hover": {
                  backgroundColor:
                    pageGroups[rowIndex + 1].page === currentPage
                      ? "#f57c00"
                      : "rgba(0, 0, 0, 0.12)",
                },
              }}
            />
          </td>
        )}

        {/* Regular table cells */}
        {row.map((cell, colIndex) => {
          const originalTableRowIndex =
            extractOriginalIndexFromId(rowIds?.[rowIndex]) || rowIndex;

          // Check if cell has been edited by comparing with original table data
          const originalRow = selectedTable?.rows?.[originalTableRowIndex];
          const originalCellText = originalRow?.[colIndex];
          const isEdited = cell !== originalCellText;

          // Apply confidence filtering to cell text if enabled AND not edited
          let displayText = cell;
          if (
            !isEdited &&
            confidenceFilterEnabled &&
            confidenceThreshold !== undefined &&
            confidenceThreshold !== null
          ) {
            displayText = getCellTextWithConfidenceFilter(
              selectedTable,
              originalTableRowIndex + 1,
              colIndex,
              confidenceThreshold,
              true
            );
          }

          // Get confidence-based styling (only consider non-filtered words for coloring)
          const lowestConfidence = showConfidence
            ? getCellLowestConfidence(
                selectedTable,
                originalTableRowIndex + 1,
                colIndex,
                confidenceThreshold,
                confidenceFilterEnabled
              )
            : null;
          const confidenceColors =
            lowestConfidence !== null
              ? getConfidenceColor(lowestConfidence)
              : null;
          const isSelected =
            selectedCell?.row === rowIndex + 1 &&
            selectedCell?.col === colIndex;

          // Determine background color
          let backgroundColor = "transparent";
          if (isSelected) {
            backgroundColor = "#e3f2fd";
          } else if (showConfidence && confidenceColors) {
            backgroundColor = confidenceColors.backgroundColor;
          }

          return (
            <td
              key={colIndex}
              data-row={rowIndex + 1}
              data-col={colIndex}
              onClick={() =>
                handleCellClick && handleCellClick(rowIndex + 1, colIndex, cell)
              }
              style={{
                ...cellStyle,
                backgroundColor,
                maxWidth: isSelected ? "300px" : "200px",
                cursor: "pointer",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {/* Delete row button - only show on first column */}
                {colIndex === 0 && tableData.rows.length > 1 && (
                  <IconButton
                    size={"small"}
                    onClick={(e) => {
                      e.stopPropagation();
                      removeRow(rowIndex, currentPage);
                    }}
                    sx={{
                      minWidth: "16px",
                      width: "16px",
                      height: "16px",
                      color: "grey.500",
                      backgroundColor: "rgba(0, 0, 0, 0.04)",
                      borderWidth: "1px",
                      borderStyle: "solid",
                      borderColor: "rgba(0, 0, 0, 0.08)",
                      borderRadius: "50%",
                      flexShrink: 0,
                      "&:hover": {
                        backgroundColor: "error.light",
                        color: "error.main",
                        borderColor: "error.main",
                      },
                    }}
                    title={`Remove row ${rowIndex + 1}`}
                  >
                    <Typography
                      sx={{
                        fontSize: "9px",
                        fontWeight: "bold",
                        lineHeight: 1,
                      }}
                    >
                      {"−"}
                    </Typography>
                  </IconButton>
                )}
                {/* Cell content */}
                <Box
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <CellContent
                    content={displayText}
                    rowIndex={rowIndex + 1}
                    colIndex={colIndex}
                    editingCell={editingCell}
                    selectedCell={selectedCell}
                    editValue={editValue}
                    setEditValue={setEditValue}
                    saveCellEdit={saveCellEdit}
                    cancelCellEdit={cancelCellEdit}
                    startCellEdit={startCellEdit}
                    setCellInfoAnchor={setCellInfoAnchor}
                  />
                </Box>
              </Box>
            </td>
          );
        })}
      </tr>
    ));
  }, [
    selectedTable,
    tableData,
    showPageIndicators,
    pageGroups,
    showConfidence,
    rowIds,
    handleCellClick,
    removeRow,
    editingCell,
    selectedCell,
    editValue,
    setEditValue,
    saveCellEdit,
    cancelCellEdit,
    startCellEdit,
    setCellInfoAnchor,
    setCurrentPage,
    cellStyle,
    borderColor,
    currentPage,
    confidenceFilterEnabled,
    confidenceThreshold,
  ]);

  if (!selectedTable || !tableData) {
    return (
      <Box sx={{ p: 3, textAlign: "center", color: "text.secondary" }}>
        <TableChart sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
        <Typography>{"Select a table to view its content"}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Excel Grid Navigation Bar */}
      <ExcelGridNavigationBar
        showStats={showStats}
        setShowStats={setShowStats}
        showConfidence={showConfidence}
        setShowConfidence={setShowConfidence}
        tableZoom={tableZoom}
        setTableZoom={setTableZoom}
        confidenceFilterEnabled={confidenceFilterEnabled}
        setConfidenceFilterEnabled={setConfidenceFilterEnabled}
        confidenceThreshold={confidenceThreshold}
        setConfidenceThreshold={setConfidenceThreshold}
        deletedRows={deletedRows}
        deletedColumns={deletedColumns}
        recoverRowById={recoverRowById}
        recoverColumnById={recoverColumnById}
        recoverAllRows={recoverAllRows}
        recoverAllColumns={recoverAllColumns}
        tableData={tableData}
        selectedTable={selectedTable}
      />

      {/* Confidence Legend - Sticky below navigation bar */}
      {showConfidence && (
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 10,
            backgroundColor: "#fff",
            borderBottom: `1px solid ${borderColor}`,
            py: 0.5,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1.5,
                alignItems: "center",
                border: "1px solid #d0d0d0",
                borderRadius: "4px",
                px: 1.5,
                py: 0.4,
                backgroundColor: "#fafafa",
              }}
            >
              <Typography
                variant={"caption"}
                color={"text.primary"}
                sx={{
                  fontWeight: 600,
                  fontSize: "0.7rem",
                  textTransform: "uppercase",
                  letterSpacing: "0.3px",
                }}
              >
                {"Extraction Confidence Score"}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Box
                  sx={{
                    width: "12px",
                    height: "12px",
                    borderRadius: "2px",
                    backgroundColor: "rgba(211, 47, 47, 0.25)",
                    border: "1px solid rgba(183, 28, 28, 0.7)",
                  }}
                />
                <Typography
                  variant={"caption"}
                  color={"text.secondary"}
                  sx={{ fontSize: "0.7rem" }}
                >
                  {"Low (<50%)"}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Box
                  sx={{
                    width: "12px",
                    height: "12px",
                    borderRadius: "2px",
                    backgroundColor: "rgba(255, 193, 7, 0.25)",
                    border: "1px solid rgba(255, 152, 0, 0.7)",
                  }}
                />
                <Typography
                  variant={"caption"}
                  color={"text.secondary"}
                  sx={{ fontSize: "0.7rem" }}
                >
                  {"Medium (50-89%)"}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Box
                  sx={{
                    width: "12px",
                    height: "12px",
                    borderRadius: "2px",
                    backgroundColor: "transparent",
                    border: `1px solid ${borderColor}`,
                  }}
                />
                <Typography
                  variant={"caption"}
                  color={"text.secondary"}
                  sx={{ fontSize: "0.7rem" }}
                >
                  {"High (≥90%)"}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      )}

      {/* Table content container with proper zoom handling */}
      <Box
        sx={{
          height: "60vh",
          overflow: "auto",
          pt: 2,
        }}
      >
        <Box
          sx={{
            transform: `scale(${tableZoom})`,
            transformOrigin: "top left",
            ...(tableZoom < 1.0 && {
              width: `${100 / tableZoom}%`, // Compensate width for zoom out
              height: `${100 / tableZoom}%`, // Compensate height for zoom out
            }),
          }}
        >
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              backgroundColor: "#fff",
              fontSize: `${14 * tableZoom}px`,
            }}
          >
            <thead>
              {/* Stats Rows */}
              <SummaryStats
                showStats={showStats}
                tableData={tableData}
                calculateColumnStats={calculateColumnStats}
                showPageIndicators={showPageIndicators}
              />

              {/* Header Row */}
              <tr>
                {/* Page indicator header */}
                {showPageIndicators && (
                  <th
                    style={{
                      borderWidth: "1px",
                      borderStyle: "solid",
                      borderColor,
                      padding: "8px",
                      fontWeight: "normal",
                      backgroundColor: "#f0f0f0",
                      width: "48px",
                      textAlign: "center",
                      position: "sticky",
                      top: showStats ? "128px" : "0px",
                      left: 0,
                      zIndex: 6,
                      fontSize: "12px",
                    }}
                  >
                    {"Page"}
                  </th>
                )}
                {(tableData.headers || []).map((header, colIndex) => (
                  <th
                    key={colIndex}
                    data-row={"0"}
                    data-col={colIndex}
                    onClick={() => handleCellClick(0, colIndex, header)}
                    style={{
                      borderWidth: "1px",
                      borderStyle: "solid",
                      borderColor,
                      padding: "12px",
                      fontWeight: "bold",
                      backgroundColor:
                        selectedCell?.row === 0 &&
                        selectedCell?.col === colIndex
                          ? "#e3f2fd"
                          : "#f5f5f5",
                      maxWidth:
                        selectedCell?.row === 0 &&
                        selectedCell?.col === colIndex
                          ? "300px"
                          : "200px",
                      transition: "background-color 0.2s, max-width 0.2s",
                      position: "sticky",
                      top: showStats ? "128px" : "0px",
                      zIndex: 4,
                      cursor: "pointer",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {/* Delete column button on the left */}
                      {tableData.headers.length > 1 && (
                        <IconButton
                          size={"small"}
                          onClick={(e) => {
                            e.stopPropagation();
                            removeColumnGlobally(colIndex);
                          }}
                          sx={{
                            minWidth: "18px",
                            width: "18px",
                            height: "18px",
                            color: "grey.500",
                            backgroundColor: "rgba(0, 0, 0, 0.04)",
                            borderWidth: "1px",
                            borderStyle: "solid",
                            borderColor: "rgba(0, 0, 0, 0.08)",
                            borderRadius: "50%",
                            flexShrink: 0,
                            "&:hover": {
                              backgroundColor: "error.light",
                              color: "error.main",
                              borderColor: "error.main",
                            },
                          }}
                          title={`Remove column ${colIndex + 1}`}
                        >
                          <Typography
                            sx={{
                              fontSize: "10px",
                              fontWeight: "bold",
                              lineHeight: 1,
                            }}
                          >
                            {"−"}
                          </Typography>
                        </IconButton>
                      )}
                      {/* Cell content */}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <CellContent
                          content={header}
                          rowIndex={0}
                          colIndex={colIndex}
                          editingCell={editingCell}
                          selectedCell={selectedCell}
                          editValue={editValue}
                          setEditValue={setEditValue}
                          saveCellEdit={saveCellEdit}
                          cancelCellEdit={cancelCellEdit}
                          startCellEdit={startCellEdit}
                          setCellInfoAnchor={setCellInfoAnchor}
                        />
                      </Box>
                    </Box>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>{memoizedTableRows}</tbody>
          </table>
        </Box>
      </Box>
    </Box>
  );
};

export default memo(ExcelGrid);
